/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-07-24
 */

// Add local dependencies.
import { store } from '../store';
import { selectIsAuthenticated, selectCsrfToken } from '../features/auth';

/**
 * Check if user is authenticated via session.
 * This checks the Redux store state rather than localStorage.
 * @returns True if user is authenticated, false otherwise.
 * @example
 * if (isSessionAuthenticated()) {
 *   // User is logged in via session
 * }
 */
export const isSessionAuthenticated = (): boolean => {
  const state = store.getState();
  return selectIsAuthenticated(state);
};

/**
 * Get the current CSRF token from the Redux store.
 * @returns The CSRF token or null if not available.
 * @example
 * const csrfToken = getCsrfToken();
 * if (csrfToken) {
 *   // Use token in request headers
 * }
 */
export const getCsrfToken = (): string | null => {
  const state = store.getState();
  return selectCsrfToken(state);
};

/**
 * Check if a CSRF token is available.
 * @returns True if CSRF token exists, false otherwise.
 * @example
 * if (hasCsrfToken()) {
 *   // Safe to make state-changing requests
 * }
 */
export const hasCsrfToken = (): boolean => getCsrfToken() !== null;

/**
 * Get authentication headers for API requests.
 * This includes the CSRF token if available.
 * @returns Headers object with authentication data.
 * @example
 * const headers = getAuthHeaders();
 * fetch('/api/endpoint', { headers });
 */
export const getAuthHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {};

  const csrfToken = getCsrfToken();
  if (csrfToken) {
    headers['X-CSRFToken'] = csrfToken;
  }

  return headers;
};

/**
 * Check if the current session is likely expired.
 * This is a best-effort check based on stored expiry time.
 * @returns True if session appears expired, false otherwise.
 * @example
 * if (isSessionExpired()) {
 *   // Redirect to login or refresh session
 * }
 */
export const isSessionExpired = (): boolean => {
  const state = store.getState();
  const { sessionExpiry } = state.auth;

  if (!sessionExpiry) {
    return false; // No expiry set, assume valid
  }

  return Date.now() > sessionExpiry;
};

/**
 * Calculate session expiry time based on duration.
 * @param durationMinutes - Session duration in minutes (default: 60)
 * @returns Timestamp when session expires.
 * @example
 * const expiry = calculateSessionExpiry(30); // 30 minutes from now
 */
export const calculateSessionExpiry = (durationMinutes: number = 60): number =>
  Date.now() + durationMinutes * 60 * 1000;

/**
 * Format session expiry time for display.
 * @param expiry - Expiry timestamp
 * @returns Formatted expiry string.
 * @example
 * const formatted = formatSessionExpiry(sessionExpiry);
 * console.log(`Session expires: ${formatted}`);
 */
export const formatSessionExpiry = (expiry: number | null): string => {
  if (!expiry) {
    return 'No expiry set';
  }

  const date = new Date(expiry);
  return date.toLocaleString();
};
